import { graphql } from 'react-relay';
import type { Disposable, IEnvironment } from 'relay-runtime';
import { requestSubscription } from 'relay-runtime';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	type ADFFetchProvider,
	type BlockSubscriptionErrorCallback,
	type BlockUpdateCallback,
	generateBlockAriFromReference,
	type ResourceId,
	type SyncBlockInstance,
	type SyncBlockProduct,
	type SyncBlockStatus,
	type Unsubscribe,
} from '@atlaskit/editor-synced-block-provider';

import { fg } from '@confluence/feature-gating';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';

import type { relaySyncBlockFetchProviderSubscription } from './__generated__/relaySyncBlockFetchProviderSubscription.graphql.ts';

/**
 * Gets the Relay environment from the global window object.
 * Confluence stores the singleton environment in window['GLOBAL_RELAY_ENV'].
 */
function getGlobalRelayEnvironment(): IEnvironment | null {
	if (typeof window !== 'undefined') {
		const globalWindow = window as unknown as { GLOBAL_RELAY_ENV?: IEnvironment };
		if (globalWindow.GLOBAL_RELAY_ENV) {
			return globalWindow.GLOBAL_RELAY_ENV;
		}
	}
	return null;
}

/**
 * Converts a Relay subscription response to SyncBlockInstance format.
 */
function convertRelayResponseToSyncBlockInstance(
	response: NonNullable<
		relaySyncBlockFetchProviderSubscription['response']['blockService_onBlockUpdated']
	>,
	resourceId: ResourceId,
): SyncBlockInstance {
	let createdAt: string | undefined;
	if (response.createdAt !== undefined && response.createdAt !== null) {
		try {
			createdAt = new Date(response.createdAt).toISOString();
		} catch {
			createdAt = undefined;
		}
	}

	let contentUpdatedAt: string | undefined;
	if (response.contentUpdatedAt !== undefined && response.contentUpdatedAt !== null) {
		try {
			contentUpdatedAt = new Date(response.contentUpdatedAt).toISOString();
		} catch {
			contentUpdatedAt = undefined;
		}
	}

	return {
		data: {
			content: JSON.parse(response.content) as ADFEntity[],
			contentUpdatedAt,
			resourceId: response.blockAri,
			blockInstanceId: response.blockInstanceId,
			sourceAri: response.sourceAri,
			product: response.product as SyncBlockProduct,
			createdAt,
			createdBy: response.createdBy,
			status: response.status as SyncBlockStatus,
		},
		resourceId,
	};
}

/**
 * Creates a Relay-based ADFFetchProvider wrapper that adds Relay subscription support
 * to an existing fetch provider.
 *
 * @param baseProvider - The base ADFFetchProvider to wrap (e.g., BlockServiceADFFetchProvider)
 * @param cloudId - The cloud ID for constructing block ARIs
 * @returns A new ADFFetchProvider with Relay subscription support
 */
export function createRelaySyncBlockFetchProvider(
	baseProvider: ADFFetchProvider,
	cloudId: string,
): ADFFetchProvider {
	const subscription = graphql`
		subscription relaySyncBlockFetchProviderSubscription($resourceId: ID!) {
			blockService_onBlockUpdated(resourceId: $resourceId) {
				blockAri
				blockInstanceId
				content
				contentUpdatedAt
				createdAt
				createdBy
				deletionReason
				product
				sourceAri
				status
			}
		}
	`;

	// Create a new provider that preserves all methods from baseProvider
	// Preserve the prototype chain to ensure all methods are accessible
	const wrappedProvider = Object.create(Object.getPrototypeOf(baseProvider));
	Object.assign(wrappedProvider, baseProvider);
	// Override subscribeToBlockUpdates with Relay subscription support
	wrappedProvider.subscribeToBlockUpdates = function (
		resourceId: ResourceId,
		onUpdate: BlockUpdateCallback,
		onError?: BlockSubscriptionErrorCallback,
	): Unsubscribe {
		// Get Relay environment: gate on → use getRelayEnvironment() which always initialises;
		// gate off → fall back to reading window.GLOBAL_RELAY_ENV (old behaviour)
		const environment = fg('platform_synced_block_patch_11')
			? getRelayEnvironment()
			: getGlobalRelayEnvironment();
		if (!environment) {
			onError?.(new Error('Relay environment not available'));
			return () => {};
		}

		// Convert resourceId to blockAri for the subscription
		const blockAri = generateBlockAriFromReference({ cloudId, resourceId });

		let disposable: Disposable | null = null;

		try {
			disposable = requestSubscription<relaySyncBlockFetchProviderSubscription>(environment, {
				subscription,
				variables: { resourceId: blockAri },
				onNext: (response) => {
					if (response?.blockService_onBlockUpdated) {
						const syncBlockInstance = convertRelayResponseToSyncBlockInstance(
							response.blockService_onBlockUpdated,
							resourceId,
						);
						onUpdate(syncBlockInstance);
					}
				},
				onError: (error) => {
					// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
					onError?.(error);
				},
			});
		} catch (error) {
			onError?.(error instanceof Error ? error : new Error(String(error)));
			// Return a no-op unsubscribe if subscription fails
			return () => {};
		}

		return () => {
			if (disposable) {
				disposable.dispose();
			}
		};
	};

	return wrappedProvider;
}
